import { useEffect, useState } from "react";
import axios from "axios";
import { TextField, Grid, MenuItem, CircularProgress, Snackbar, Alert, Box, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { FEMALE_PROFILE } from "../../../constants";
import CardMarkerSmall from "../../../components/CockpitCards/CardMarkerSmall";
import ButtonForm from "../../../components/Button/ButtonForm";
import { useTranslation } from "../../../language";

const STATUS_OPTIONS = {
  cycletracker:  ["normal", "hormonal_birth_control"],
  perimenopause: ["normal", "hrt"],
  postmenopause: ["normal", "hrt"],
};

export default function FemaleProfileCard() {

  const theme = useTheme();
  const { t } = useTranslation();

  const [currentProfile, setCurrentProfile] = useState("");
  const [currentStatus, setCurrentStatus]   = useState("normal");
  const [selectedProfile, setSelectedProfile] = useState("");
  const [selectedStatus, setSelectedStatus]   = useState("normal");
  const [profileExists, setProfileExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    async function fetchFemaleProfile() {
      try {
        const res = await axios.get(FEMALE_PROFILE);
        setCurrentProfile(res.data.profile);
        setCurrentStatus(res.data.status ?? "normal");
        setSelectedProfile(res.data.profile);
        setSelectedStatus(res.data.status ?? "normal");
        setProfileExists(true);
      } catch (err) {
        if (err.response?.status === 404) {
          setProfileExists(false);
        } else {
          setErrorMessage(t("ua_female_load_error"));
        }
      } finally {
        setLoading(false);
      }
    }
    fetchFemaleProfile();
  }, []);

  const handleProfileChange = (newProfile) => {
    setSelectedProfile(newProfile);
    const validStatuses = STATUS_OPTIONS[newProfile] ?? [];
    if (!validStatuses.includes(selectedStatus)) {
      setSelectedStatus("normal");
    }
  };

  const handleSave = async () => {
    setSubmitting(true);
    const payload = { profile: selectedProfile, status: selectedStatus };
    try {
      if (profileExists) {
        await axios.patch(FEMALE_PROFILE, payload);
      } else {
        await axios.post(FEMALE_PROFILE, payload);
        setProfileExists(true);
      }
      setCurrentProfile(selectedProfile);
      setCurrentStatus(selectedStatus);
      setSuccessOpen(true);
    } catch (err) {
      if (err.response?.status === 400 && !profileExists) {
        try {
          await axios.patch(FEMALE_PROFILE, payload);
          setCurrentProfile(selectedProfile);
          setCurrentStatus(selectedStatus);
          setProfileExists(true);
          setSuccessOpen(true);
        } catch {
          setErrorMessage(t("ua_female_update_error"));
        }
      } else {
        setErrorMessage(err.response?.data?.detail || t("ua_female_update_error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isDark = theme.palette.mode === "dark";
  const glassCardSx = {
    bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
    backdropFilter: "blur(28px) saturate(180%)",
    WebkitBackdropFilter: "blur(28px) saturate(180%)",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
    boxShadow: isDark
      ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
      : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
  };

  const cardSx = {
    p: 4, borderRadius: 3, overflow: "hidden",
    ...glassCardSx,
  };

  const isUnchanged = selectedProfile === currentProfile && selectedStatus === currentStatus;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Grid size={{ xs: 12 }} sx={{ maxWidth: 800, width: "100%", px: { xs: 2, sm: 0 }, display: "flex", justifyContent: "center", alignItems: "center" }}>

      <CardMarkerSmall
        headline={t("ua_female_headline")}
        subtext={t("ua_female_subtext")}
        sx={glassCardSx}
        cardlines={
          <Grid container sx={cardSx}>

            <Grid size={12}>
              <TextField
                fullWidth select size="small" margin="normal"
                label={t("ua_female_profile_label")}
                value={selectedProfile}
                onChange={(e) => handleProfileChange(e.target.value)}
              >
                <MenuItem value="cycletracker">{t("ua_female_opt_cycletracker")}</MenuItem>
                <MenuItem value="perimenopause">{t("ua_female_opt_perimenopause")}</MenuItem>
                <MenuItem value="postmenopause">{t("ua_female_opt_postmenopause")}</MenuItem>
              </TextField>
            </Grid>

            {selectedProfile && (
              <Grid size={12}>
                <TextField
                  fullWidth select size="small" margin="normal"
                  label={t("ua_female_status_label")}
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  {(STATUS_OPTIONS[selectedProfile] ?? []).map((s) => (
                    <MenuItem key={s} value={s}>{t(`ua_female_status_${s}`)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid size={12} sx={{ my: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <ButtonForm
                name={submitting ? <CircularProgress size={18} color="inherit" /> : t("ua_female_save")}
                onClick={handleSave}
                disabled={submitting || !selectedProfile || isUnchanged}
              />
            </Grid>

          </Grid>
        }
      />

      <Snackbar open={successOpen} autoHideDuration={4000} onClose={() => setSuccessOpen(false)}>
        <Alert severity="success" variant="filled" sx={{ width: "100%" }}>
          <Typography variant="h6">{t("ua_female_success")}</Typography>
        </Alert>
      </Snackbar>

      <Snackbar open={Boolean(errorMessage)} autoHideDuration={5000} onClose={() => setErrorMessage("")}>
        <Alert severity="error" variant="filled" sx={{ width: "100%" }}>
          <Typography variant="h6">{errorMessage}</Typography>
        </Alert>
      </Snackbar>

    </Grid>
  );
}
